import React, { useContext, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { TISRProject, TISRProjects, TPushToEnvironmentPayload, TEnvironmentOption, TransferModelOwnerPayload } from './DefineModel/model';
import { useNavigate } from "react-router";
import { ChangeIcon, CopyIcon, DeleteIcon, OverFlowIcon, UpArrowGreenIcon } from 'assests/GmsIcons';
import { formatDate } from 'Common/HelperClass';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Status from 'Components/DesignSystem/Status/Status';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import { AppContext } from 'Context/AppContext';
import ChangeOwnerDialog, { User } from 'Pages/GMS/Rules/ChangeOwnerDialog';
import { copyModel, transferModelOwner, bulkDeleteISRProjects, bulkTransferModelOwner, bulkApplyCSS } from './DefineModel/ModelServices/ModalServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import PushToEnvironmentDialog from './PushToEnvironmentDialog';
import AuthManager from 'Common/AuthManager';
import CopyModal from 'Pages/GMS/ARS/Components/CopyModal';
import CopyableId from 'Components/Common/CopyableId/CopyableId';
import Button from 'Components/DesignSystem/Button/Button';
import Alert from 'Components/DesignSystem/Alert/Alert';
import BulkDeleteModal from './BulkDeleteModal';
import BulkApplyCSSModal from './BulkApplyCSSModal';


type TISRProjectTableProps = {
    isrProjects: TISRProjects;
    setDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteItem: React.Dispatch<React.SetStateAction<{ id: string, name: string }>>;
    isGuide: boolean;
    updatePermission: boolean;
    fetchISRProjects: () => Promise<void>;
    sortBy: string;
    sortOrder: string;
    onSort: (column: string) => void;
    industryId: string;
    environmentOptions: TEnvironmentOption[];
    onPushToEnvironment: (payload: TPushToEnvironmentPayload) => Promise<{ done: boolean; message?: string }>;
    /** § 87: the page-level fetch spinner now renders as the DS Table's own
     *  loading treatment (skeleton rows on first load, dim + "Refreshing" chip
     *  on a refetch) instead of a separate full-width skeleton element. */
    loading?: boolean;
}

/**
 * The Guide/ISR models grid on the DS `Table` (§ 87, same recipe as the
 * Lists grid § 81). Sort stays fully controlled and two-state: the parent's
 * `handleSort` owns the asc/desc toggle (and the per-column default
 * direction), so the DS header's own asc→desc→null cycle never applies — we
 * ignore the direction it suggests and forward only the column id.
 *
 * The row-overflow menu keeps the § 81 portal-to-body positioning (the table
 * wrapper is an overflow container, which would clip an absolutely-positioned
 * popover) with the DS `Menu` inside: one real menu of menuitems — the legacy
 * `OverflowOption` rendered `role="menu"` once PER item — with Escape/outside
 * dismissal via `onDismiss` and true `disabled` semantics on the gated Delete
 * (legacy used opacity + pointer-events on a `<Link to="#">`).
 *
 * The status chips are the DS `Status` — the component whose deprecated
 * `promoted`/`notPromoted` aliases were minted for exactly this column. The
 * local `PromotedBadge` stays alive for `UIModelTable` in the builder; this
 * page just stops consuming it. `showAlert`/`alertLabel` give the unpromoted-
 * changes triangle the accessible name the legacy wordless glyph never had.
 */
const ISRProjectTable = ({ isrProjects, setDeleteModal, setDeleteItem, isGuide, updatePermission, fetchISRProjects, sortBy, sortOrder, onSort, environmentOptions, industryId, onPushToEnvironment, loading = false }: TISRProjectTableProps) => {

    const [selectIndex, setSelectIndex] = useState<number | null>(null);
    // right-anchored like § 81: the menu hangs leftward from the kebab.
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const [showOwnerTransferDialog, setShowOwnerTransferDialog] = useState<boolean>(false);
    const [showPushToEnvironmentDialog, setShowPushToEnvironmentDialog] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<TISRProject | null>(null);
    const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
    const [copyModelName, setCopyModelName] = useState<string>('');
    const [copyItem, setCopyItem] = useState<TISRProject | null>(null);
    const [copyInProgress, setCopyInProgress] = useState<boolean>(false);
    const [copyError, setCopyError] = useState<string>('');
    const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [showBulkApplyCSSModal, setShowBulkApplyCSSModal] = useState(false);
    const [showBulkChangeOwnerModal, setShowBulkChangeOwnerModal] = useState(false);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const navigate = useNavigate();
    const navigateToMainPage = (name: string, projectId: string = '') => {
        if (!updatePermission) {
            toast.warning('You do not have permission to edit this model');
            return;
        }
        // Bug 12453: model names can contain special characters (#, %, spaces, …);
        // encode the name so the route is valid and resolves correctly.
        if (isGuide) {
            navigate(`/gms/guide/main/${encodeURIComponent(name)}/${projectId}`);
        } else {
            navigate(`/gms/isr/main/${encodeURIComponent(name)}/${projectId}`);
        }
    }

    const appData = useContext(AppContext);
    const hasCreatePermission = () => (appData?.pm?.checkPermission("gmsguide_create"))
    const hasOwnerTransferPermission = () => (appData?.pm?.checkPermission("gmsguide_transfer"))
    const hasPushPermission = () => (appData?.pm?.checkPermission("gmsguide_push"))
    // AB#11595: the Guide-list multi-select checkbox column, the bulk action bar,
    // and each individual bulk button are gated on their OWN RBAC action, not on
    // the single-model permissions. SAMS grants these only to Compatio SuperAdmin
    // and Guide Admin — so every other role sees the list without any checkbox
    // column and without a bulk bar. The per-row overflow menu (Change Owner,
    // Delete) stays on the single-model permissions above.
    const hasBulkTransferPermission = () => (appData?.pm?.checkPermission("gmsguide_bulktransfer"))
    const hasBulkDeletePermission = () => (appData?.pm?.checkPermission("gmsguide_bulkdelete"))
    const hasBulkUpdatePermission = () => (appData?.pm?.checkPermission("gmsguide_bulkupdate"))
    const closeOwnerTransferDialog = () => setShowOwnerTransferDialog(false);
    const currentUserId = String(AuthManager.getAuthUser()?.profile?.id ?? '');
    const isOwner = (row: TISRProject) => !!currentUserId && row?.createdBy === currentUserId;

    const showBulkBar = hasBulkTransferPermission() || hasBulkDeletePermission() || hasBulkUpdatePermission();
    const selectedModels = (isrProjects ?? []).filter(p => selectedKeys.includes(p.id));
    const liveCount = isGuide ? selectedModels.filter(m => m.status === 'Live').length : 0;
    const hasLiveSelected = liveCount > 0;
    // Bulk action bar (button row ~36px + mb-3 12px) + live banner (~36px + mb-3 12px when shown)
    // both sit above the Table and reduce the space available for the sticky-header scroll box.
    // PAGE_GRID_MAX_HEIGHT is calibrated for 200px of chrome; subtract the added elements
    // so the Table's own scroll container stays bounded and the header stays sticky.
    const bulkBarHeight = isGuide && showBulkBar ? 48 : 0;
    const liveBannerExtraHeight = isGuide && showBulkBar && hasLiveSelected && selectedModels.length > 0 ? 48 : 0;
    const tableMaxHeight = bulkBarHeight > 0
        ? `calc(100vh - ${200 + bulkBarHeight + liveBannerExtraHeight}px)`
        : PAGE_GRID_MAX_HEIGHT;
    const liveBannerMessage = selectedModels.length === 1 && liveCount === 1
        ? 'Selected Model is Live and cannot be deleted'
        : liveCount === selectedModels.length
        ? 'Selected Models are Live and cannot be deleted'
        : 'One or more of the selected Models is Live and cannot be deleted';

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 2, right: window.innerWidth - rect.right });
        setSelectIndex(prev => (prev === index ? null : index));
    };

    const ownerChangeSuccessMessage = (user: string) => `Model owner changed to ${user}. They can now edit and manage this model.`;
    const ownerChangeFailureMessage = "Model owner could not be changed";

    const onOwnerChange = async (data: TransferModelOwnerPayload) => {
        try {
            const response = await transferModelOwner(data)
            if (response?.data?.done) { toast.success(ownerChangeSuccessMessage(`${data.username}`)); }
            else { toast.error(ownerChangeFailureMessage) }
            closeOwnerTransferDialog()
        } catch (error) {
            console.log('Error', error);
            toast.error(ownerChangeFailureMessage)
        }
        finally {
            fetchISRProjects()
        }
    }

    const channelKey = selectedModels[0]?.channel ?? '';

    const handleBulkChangeOwner = async (user: User) => {
        setBulkActionLoading(true);
        try {
            const response = await bulkTransferModelOwner(selectedModels.map(m => m.id), user.id, `${user.first_name} ${user.last_name}`, channelKey);
            if (response?.data?.done) {
                const updatedCount: number = response.data.updated_count ?? selectedModels.length;
                toast.success(`Owner changed for ${updatedCount} model(s).`);
                setSelectedKeys([]);
            } else {
                toast.error(response?.data?.message || 'Owner could not be changed');
            }
        } catch { toast.error('Owner could not be changed'); }
        finally { setBulkActionLoading(false); setShowBulkChangeOwnerModal(false); fetchISRProjects(); }
    };

    const handleBulkDelete = async () => {
        setBulkActionLoading(true);
        try {
            const response = await bulkDeleteISRProjects(selectedModels.map(m => m.id), channelKey);
            if (response?.data?.done) {
                const deletedCount: number = response.data.deleted_count ?? 0;
                const skippedCount: number = response.data.skipped_count ?? 0;
                // The BE returns `not_owner_count` (and a `skipped_reasons.not_owner`
                // list) when models were skipped specifically because the caller is
                // not their owner — bulk delete is owner-locked, unlike the row-level
                // delete which only checks GMSGuide:delete. Call that case out
                // explicitly so the user understands why their selection partially
                // (or fully) failed, rather than seeing a generic "live or not found".
                const notOwnerCount: number = response.data.not_owner_count
                    ?? response.data.skipped_reasons?.not_owner?.length
                    ?? 0;
                const ownerSuffix = notOwnerCount > 0
                    ? ` ${notOwnerCount} skipped — only the model owner has delete permission.`
                    : '';
                if (deletedCount > 0 && skippedCount > 0) {
                    toast.success(`${deletedCount} model(s) deleted. ${skippedCount} skipped.${ownerSuffix}`);
                } else if (deletedCount > 0) {
                    toast.success(`${deletedCount} model(s) deleted.`);
                } else if (notOwnerCount > 0 && notOwnerCount === skippedCount) {
                    // Pure ownership rejection — raise it to the user plainly.
                    toast.warning('Only the model owner has delete permission.');
                } else {
                    toast.warning(`No models deleted — ${skippedCount} skipped (live or not found).${ownerSuffix}`);
                }
                setSelectedKeys([]);
            } else {
                toast.error(response?.data?.message || 'Models could not be deleted');
            }
        } catch { toast.error('Models could not be deleted'); }
        finally { setBulkActionLoading(false); setShowBulkDeleteModal(false); fetchISRProjects(); }
    };

    const handleBulkApplyCSS = async (css: string, mode: 'overwrite' | 'append') => {
        setBulkActionLoading(true);
        try {
            const response = await bulkApplyCSS(selectedModels.map(m => m.id), css, mode, channelKey);
            if (response?.data?.done) {
                const updatedCount: number = response.data.updated_count ?? 0;
                const verb = mode === 'overwrite' ? 'overwritten' : 'appended';
                toast.success(`CSS ${verb} for ${updatedCount} UI model(s).`);
                setSelectedKeys([]);
            } else {
                toast.error(response?.data?.message || 'CSS could not be applied');
            }
        } catch { toast.error('CSS could not be applied'); }
        finally { setBulkActionLoading(false); setShowBulkApplyCSSModal(false); fetchISRProjects(); }
    };

    useEffect(() => {
        // A fixed-position menu detaches from its row the moment anything
        // scrolls, so scrolling closes it. Outside-click and Escape are the
        // DS Menu's own job (`onDismiss`).
        function handleScroll() {
            if (selectIndex != null) {
                setSelectIndex(null);
            }
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    const onClickedChangeOwner = (item: TISRProject) => {
        setShowOwnerTransferDialog(true);
        setSelectedItem(item);
    }

    const onClickedCopy = (item: TISRProject) => {
        setCopyModelName(`Copy of ${item?.ISRName ?? 'Model'}`);
        setCopyItem(item);
        setCopyError('');
        setShowCopyModal(true);
    }

    const closeCopyModal = () => {
        setShowCopyModal(false);
        setCopyItem(null);
        setCopyModelName('');
        setCopyError('');
    }

    // Clear the inline error as soon as the user edits the name
    const onCopyModelNameChange = (name: string) => {
        setCopyError('');
        setCopyModelName(name);
    }

    const onConfirmCopy = async () => {
        if (!copyItem || copyInProgress) return;
        const name = copyModelName.trim();
        if (!name) {
            setCopyError('Please enter a model name');
            return;
        }
        setCopyError('');
        setCopyInProgress(true);
        try {
            const response = await copyModel(copyItem.id, name)
            if (response?.data?.done) {
                toast.success(response?.data?.message || "Model copied successfully");
                closeCopyModal();
                fetchISRProjects();
            } else {
                // Keep the modal open so the user can correct the name (e.g. duplicate)
                setCopyError(response?.data?.message || "Model could not be copied");
            }
        } catch (error) {
            console.log('Error', error);
            setCopyError("Model could not be copied")
        } finally {
            setCopyInProgress(false);
        }
    }

    const renderStatusChip = (row: TISRProject, rowIndex: number) => {
        const rowKey = row?.id ?? rowIndex;
        if (row.status === 'Live') {
            return (
                <Tooltip
                    usePortal
                    placement="bottom"
                    content={row.hasUnpromotedChanges
                        ? 'This model has unpromoted changes. Promote to apply.'
                        : (row.widgetName ? `Mapped to ${row.widgetName}` : 'This model is live in production.')}
                >
                    <Status
                        tone="success"
                        label="Live"
                        showAlert={row.hasUnpromotedChanges}
                        alertLabel="Has unpromoted changes"
                        data-testid={`isr-row-status-${rowKey}`}
                    />
                </Tooltip>
            );
        }
        if (row.status === 'Promoted') {
            return (
                <Tooltip
                    usePortal
                    placement="bottom"
                    content={row.hasUnpromotedChanges
                        ? 'This model has unpromoted changes. Promote to apply.'
                        : 'This model is promoted (live)'}
                >
                    <Status
                        tone="success"
                        label="Promoted"
                        showAlert={row.hasUnpromotedChanges}
                        alertLabel="Has unpromoted changes"
                        data-testid={`isr-row-status-${rowKey}`}
                    />
                </Tooltip>
            );
        }
        // The legacy plain "Not Promoted" span had no tooltip — keep it mute.
        return <Status tone="neutral" label="Not Promoted" data-testid={`isr-row-status-${rowKey}`} />;
    };

    const renderRowActions = (row: TISRProject, rowIndex: number) => {
        const rowKey = row?.id ?? rowIndex;
        const liveGuideBlock = isGuide && row?.status === 'Live';
        const deleteEnabled = isOwner(row) && !liveGuideBlock;
        const deleteTooltip = liveGuideBlock
            ? 'Cannot delete live model. Unmap from widget first.'
            : (!isOwner(row) ? 'Only the model owner can delete this model.' : undefined);

        const menuItems: MenuItem[] = [
            /* release-sept-29's "Push to environment", kept FIRST as it shipped. The
               hand-rolled OverflowOption row it used is gone; this is the same action,
               same permission gate, same testid stem, as a DS Menu item. */
            ...(hasPushPermission() ? [{
                id: 'push-to-environment', label: 'Push to environment', icon: <UpArrowGreenIcon />,
                'data-testid': `isr-row-menu-push-to-environment-${rowKey}`,
                onClick: () => { setSelectIndex(null); setSelectedItem(row); setShowPushToEnvironmentDialog(true); },
            }] : []),
            ...(hasCreatePermission() ? [{
                id: 'copy', label: 'Copy', icon: <CopyIcon />,
                'data-testid': `isr-row-menu-copy-${rowKey}`,
                onClick: () => { setSelectIndex(null); onClickedCopy(row); },
            }] : []),
            ...(hasOwnerTransferPermission() ? [{
                id: 'change-owner', label: 'Change Owner', icon: <ChangeIcon />,
                'data-testid': `isr-row-menu-change-owner-${rowKey}`,
                onClick: () => { setSelectIndex(null); onClickedChangeOwner(row); },
            }] : []),
            {
                id: 'delete', label: 'Delete', icon: <DeleteIcon />,
                disabled: !deleteEnabled,
                ...(deleteTooltip ? { tooltip: deleteTooltip } : {}),
                'data-testid': `isr-row-menu-delete-${rowKey}`,
                onClick: () => {
                    setSelectIndex(null);
                    setDeleteItem({ id: row?.id, name: row?.ISRName });
                    setDeleteModal(true);
                },
            },
        ];

        return (
            <>
                <button
                    type="button"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={selectIndex === rowIndex}
                    data-testid={`pages-isrprojecttable-button-2-${rowIndex}`}
                    id={`isr-row-${rowIndex}-btn-overflow`}
                    className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                    onClick={(e) => handleOverflowClick(e, rowIndex)}
                >
                    <OverFlowIcon />
                </button>
                {selectIndex === rowIndex ? ReactDOM.createPortal(
                    <div
                        id={`isr-row-${rowIndex}-menu`}
                        className="tw-fixed tw-z-[9999] tw-w-52"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`isr-row-overflow-menu-${rowKey}`}
                            onDismiss={() => setSelectIndex(null)}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<TISRProject>[] = [
        {
            id: 'ISRName',
            header: 'Model Name',
            accessor: (r) => r?.ISRName ?? '',
            sortable: true,
            /* 29%, up from 25%: the 4 points come from the ID column, which stopped
               printing 32 characters of hex. Names are what people scan. */
            width: '29%',
            render: (_value, row, rowIndex) => (
                <div
                    role="button"
                    id={`model-name-${rowIndex}`}
                    data-testid={`isr-row-name-${row?.id ?? rowIndex}`}
                    className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer tw-truncate"
                    title={row?.ISRName ?? ''}
                    onClick={() => navigateToMainPage(row?.ISRName ?? '', row?.id ?? '')}
                >
                    {row?.ISRName ?? ''}
                </div>
            ),
        },
        {
            id: 'id',
            header: 'ID',
            accessor: (r) => r?.id ?? '-',
            /* 8%, down from 12%: ten characters, an ellipsis and the copy icon do not
               fill 12, and the gap read as a column that had lost its content. */
            width: '8%',
            /* Shared with the Products table (US 13549): same cell, same ids. */
            render: (_value, row, rowIndex) => (
                <CopyableId
                    id={row?.id}
                    idPrefix="model-id"
                    suffix={rowIndex}
                    copyButtonTestId={`pages-isrprojecttable-button-1-${rowIndex}`}
                />
            ),
        },
        ...(isGuide ? [{
            id: 'status',
            header: 'Status',
            accessor: (r: TISRProject) => r?.status ?? '',
            width: '12%',
            render: (_value: any, row: TISRProject, rowIndex: number) => (
                <span id={`status-${rowIndex}`}>{renderStatusChip(row, rowIndex)}</span>
            ),
        } satisfies TableColumn<TISRProject>] : []),
        {
            id: 'createdByUsername',
            header: 'Owner',
            accessor: (r) => r?.createdByUsername ?? '-',
            sortable: true,
            width: '16%',
            render: (value, _row, rowIndex) => <span id={`create-by-username-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'createdAt',
            header: 'Created',
            accessor: (r) => r?.createdAt ? formatDate(Number(r?.createdAt)) : '-',
            sortable: true,
            cellType: 'meta',
            width: '13%',
            render: (value, _row, rowIndex) => <span id={`created-at-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'updatedAt',
            header: 'Last Updated',
            accessor: (r) => formatDate(Number(r?.updatedAt ?? r?.createdAt)),
            sortable: true,
            cellType: 'meta',
            width: '13%',
            render: (value, _row, rowIndex) => <span id={`updated-at-${rowIndex}`}>{value}</span>,
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '48px',
            align: 'right',
            render: (_value, row, rowIndex) => renderRowActions(row, rowIndex),
        },
    ];

    return (
        <div id="isr-project-table" className="tw-mt-3">
            {isGuide && showBulkBar && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3" data-testid="bulk-action-bar">
                    {hasBulkTransferPermission() && (
                        <Tooltip
                            usePortal
                            placement="bottom"
                            content={selectedModels.length === 0 ? 'Select one or more models to proceed' : undefined}
                        >
                            <Button
                                variant="secondary"
                                onClick={() => { if (selectedModels.length > 0) setShowBulkChangeOwnerModal(true); }}
                                data-testid="bulk-change-owner-btn"
                            >
                                Change Owner
                            </Button>
                        </Tooltip>
                    )}
                    {hasBulkUpdatePermission() && (
                        <Tooltip
                            usePortal
                            placement="bottom"
                            content={selectedModels.length === 0 ? 'Select one or more models to proceed' : undefined}
                        >
                            <Button
                                variant="secondary"
                                onClick={() => { if (selectedModels.length > 0) setShowBulkApplyCSSModal(true); }}
                                data-testid="bulk-apply-css-btn"
                            >
                                Apply CSS
                            </Button>
                        </Tooltip>
                    )}
                    {hasBulkDeletePermission() && (
                        <Tooltip
                            usePortal
                            placement="bottom"
                            content={
                                selectedModels.length === 0
                                    ? 'Select one or more models to proceed'
                                    : hasLiveSelected
                                    ? 'Deselect Live models to delete'
                                    : undefined
                            }
                        >
                            <Button
                                variant="danger"
                                onClick={() => { if (selectedModels.length > 0 && !hasLiveSelected) setShowBulkDeleteModal(true); }}
                                data-testid="bulk-delete-btn"
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
            {isGuide && showBulkBar && hasLiveSelected && selectedModels.length > 0 && (
                <Alert tone="warning" className="tw-mb-3" data-testid="bulk-live-model-banner">
                    {liveBannerMessage}
                </Alert>
            )}
            <Table
                columns={columns}
                data={isrProjects ?? []}
                rowKey="id"
                striped
                hoverable
                loading={loading}
                /* `stickyHeader` needs `maxHeight` to mean anything — sticky resolves
                   against the nearest scrolling ancestor, and without a bounded box
                   this wrapper never scrolls (DS § 106). They ship as a pair. */
                stickyHeader
                maxHeight={tableMaxHeight}
                sortState={{ columnId: sortBy, direction: sortOrder === 'asc' ? 'asc' : 'desc' }}
                onSort={(columnId) => onSort(columnId)}
                data-testid="isr-projects-table"
                {...(isGuide && showBulkBar ? {
                    selectable: true,
                    selectedKeys,
                    onSelectionChange: setSelectedKeys,
                } : {})}
            />
            {showCopyModal ? <CopyModal
                id="isr-copy-model-modal"
                showModal={showCopyModal}
                closeModal={closeCopyModal}
                onConfirm={onConfirmCopy}
                modelName={copyModelName}
                setCopyModelName={onCopyModelNameChange}
                isCopying={copyInProgress}
                error={copyError}
            /> : null}
            {showOwnerTransferDialog ? <ChangeOwnerDialog id="isr-change-owner-dialog" dialogHeader={"Change Owner"} labelName={"Select owner"} onClose={() => { closeOwnerTransferDialog(); setSelectedItem(null) }} onSubmit={(user: User) => { if (selectedItem != null) onOwnerChange({ model_id: selectedItem.id, userid: user.id, username: `${user?.first_name} ${user?.last_name}` }) }} /> : null}
            {/* release-sept-29's push-to-environment flow. It survives the table rewrite
                intact: the trigger moved into the DS row menu above, and this is the
                dialog it opens. Outside the Table, as it was outside the old <table>. */}
            {showPushToEnvironmentDialog && selectedItem && (
                <PushToEnvironmentDialog
                    isOpen={showPushToEnvironmentDialog}
                    onClose={() => { setShowPushToEnvironmentDialog(false); setSelectedItem(null); }}
                    item={selectedItem}
                    environmentOptions={environmentOptions}
                    industryId={industryId}
                    onSubmit={onPushToEnvironment}
                />
            )}
            {showBulkDeleteModal && (
                <BulkDeleteModal
                    open={showBulkDeleteModal}
                    selectedModels={selectedModels}
                    onClose={() => setShowBulkDeleteModal(false)}
                    onConfirm={handleBulkDelete}
                    loading={bulkActionLoading}
                    currentUserId={currentUserId}
                />
            )}
            {showBulkApplyCSSModal && (
                <BulkApplyCSSModal
                    open={showBulkApplyCSSModal}
                    selectedModels={selectedModels}
                    onClose={() => setShowBulkApplyCSSModal(false)}
                    onConfirm={handleBulkApplyCSS}
                    loading={bulkActionLoading}
                />
            )}
            {showBulkChangeOwnerModal && (
                <ChangeOwnerDialog
                    id="isr-bulk-change-owner-dialog"
                    dialogHeader="Change Owner"
                    subtitle="Reassign models to new owner."
                    labelName="Select owner"
                    showCancelButton
                    onClose={() => setShowBulkChangeOwnerModal(false)}
                    onSubmit={handleBulkChangeOwner}
                    showIndicator={bulkActionLoading}
                />
            )}
        </div>
    );
}

export default ISRProjectTable;
